import React, { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import {
  X,
  Loader2,
  RefreshCw,
  Shield,
  Lock,
  Activity,
  Images,
  DollarSign,
  Percent,
  Target,
  AlertTriangle,
  Clock,
  CheckCircle2,
  XCircle,
  Layers,
  ChevronLeft,
  ChevronRight,
  User,
  Users,
  ArrowLeft,
  ExternalLink,
  Sparkles,
} from 'lucide-react';
import type { AdminRole } from '../services/supabase';
import {
  getHdrAdminOverview,
  getHdrAdminImages,
  getHdrAdminQueue,
  getHdrAdminSalesMetrics,
  getHdrAdminPipeline,
  getHdrAdminUsers,
  getHdrAdminUserDetail,
  setHdrAdminPipeline,
  getThumbnailUrl,
  type HdrAdminOverview,
  type HdrAdminImage,
  type HdrAdminQueueJob,
  type HdrAdminSalesMetrics,
  type HdrAdminUser,
  type HdrAdminUserDetail,
  type HdrAdminUserListing,
  type HdrAdminUserSort,
  type HdrPipelineSetting,
} from '../services/supabase';
import {
  adminGetHdrPlans,
  adminGetHdrProfitability,
  adminSetHdrSetting,
  adminUpsertHdrPlan,
  type HdrAdminPlanRow,
  type HdrProfitabilityMetrics,
} from '../services/hdr';
import { HDR_PRIMARY_PIPELINE_OPTIONS, UserProfile, type HdrMergePipeline } from '../types';
import HdrEngineAdmin from './HdrEngineAdmin';

interface AdminPanelProps {
  user: UserProfile;
  adminRole: AdminRole;
}

type AdminTab = 'overview' | 'users' | 'images' | 'sales' | 'engine';
type SalesPeriod = 'today' | '7d' | '30d' | 'all';

const ADMIN_TAB_TO_SLUG: Record<AdminTab, string> = {
  overview: 'processing',
  users: 'users',
  images: 'images',
  sales: 'costs',
  engine: 'hdr-engine',
};

const ADMIN_SLUG_TO_TAB: Record<string, AdminTab> = {
  processing: 'overview',
  overview: 'overview',
  users: 'users',
  images: 'images',
  costs: 'sales',
  sales: 'sales',
  'hdr-engine': 'engine',
  engine: 'engine',
};

function tabFromSlug(slug?: string): AdminTab | null {
  if (!slug) return 'overview';
  return ADMIN_SLUG_TO_TAB[slug] ?? null;
}

const USD_TO_GBP = 0.79;
const formatUsdGbp = (usd: number, decimals = 2) => {
  const gbp = usd * USD_TO_GBP;
  return {
    str: `$${usd.toFixed(decimals)} / £${gbp.toFixed(decimals)}`,
    usd,
    gbp,
  };
};

const queueStatusStyles: Record<string, string> = {
  queued: 'bg-slate-700 text-slate-300',
  processing: 'bg-blue-500/20 text-blue-300',
  completed: 'bg-emerald-500/20 text-emerald-300',
  failed: 'bg-red-500/20 text-red-300',
};

/** Pipeline → AI model/provider actually used for the enhancement. */
const PIPELINE_MODEL_LABELS: Record<string, string> = {
  autoenhance: 'Autoenhance.ai',
  autopix: 'Esoft Autopix (eWarp 3.0)',
  gemini: 'Google Gemini (gemini-3-pro-image-preview)',
  fal: 'fal.ai',
};

const AdminPanel: React.FC<AdminPanelProps> = ({ user, adminRole }) => {
  const navigate = useNavigate();
  const { section, itemId } = useParams<{ section?: string; itemId?: string }>();
  const activeTab = tabFromSlug(section);
  const selectedUserId = activeTab === 'users' ? itemId ?? null : null;

  const [loading, setLoading] = useState(false);
  const [overview, setOverview] = useState<HdrAdminOverview | null>(null);
  const [queueJobs, setQueueJobs] = useState<HdrAdminQueueJob[]>([]);
  const [queueFilter, setQueueFilter] = useState<string>('');
  const [images, setImages] = useState<HdrAdminImage[]>([]);
  const [imagesTotal, setImagesTotal] = useState(0);
  const [imagesPage, setImagesPage] = useState(0);
  const [imageSearch, setImageSearch] = useState('');
  const [imageTypeFilter, setImageTypeFilter] = useState('');
  const [imageGridCols, setImageGridCols] = useState<number>(() => {
    const saved = Number(localStorage.getItem('hdr_admin_images_grid_cols'));
    return saved >= 1 && saved <= 8 ? saved : 6;
  });
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [users, setUsers] = useState<HdrAdminUser[]>([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersPage, setUsersPage] = useState(0);
  const [userSearch, setUserSearch] = useState('');
  const [userSort, setUserSort] = useState<HdrAdminUserSort>('last_activity');
  const [userDetail, setUserDetail] = useState<HdrAdminUserDetail | null>(null);
  const [userListings, setUserListings] = useState<HdrAdminUserListing[]>([]);
  const [userListingFilter, setUserListingFilter] = useState('');

  const updateImageGridCols = (cols: number) => {
    const clamped = Math.min(8, Math.max(1, cols));
    setImageGridCols(clamped);
    localStorage.setItem('hdr_admin_images_grid_cols', String(clamped));
  };

  // Larger thumbnails when fewer columns are shown, so images stay sharp.
  const imageThumbWidth = imageGridCols <= 2 ? 800 : imageGridCols <= 4 ? 480 : 240;
  const [salesPeriod, setSalesPeriod] = useState<SalesPeriod>('7d');
  const [sales, setSales] = useState<HdrAdminSalesMetrics | null>(null);
  const [hdrProfit, setHdrProfit] = useState<HdrProfitabilityMetrics | null>(null);
  const [hdrPlans, setHdrPlans] = useState<HdrAdminPlanRow[]>([]);
  const [processingCost, setProcessingCost] = useState('0.30');
  const [gbpUsdRate, setGbpUsdRate] = useState('1.27');
  const [pricingSaving, setPricingSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pipelineSetting, setPipelineSetting] = useState<HdrPipelineSetting | null>(null);
  const [pipelineSaving, setPipelineSaving] = useState(false);
  const [pipelineMessage, setPipelineMessage] = useState<string | null>(null);

  const pageSize = 24;
  const usersPageSize = 50;
  const resolvedTab: AdminTab = activeTab ?? 'overview';

  const periodStart = (period: SalesPeriod): string | null => {
    const now = new Date();
    if (period === 'all') return null;
    if (period === 'today') {
      const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
      return start.toISOString();
    }
    const days = period === '7d' ? 7 : 30;
    return new Date(now.getTime() - days * 24 * 60 * 60 * 1000).toISOString();
  };

  const loadOverview = async () => {
    setLoading(true);
    setError(null);
    const [ov, q, pipe] = await Promise.all([
      getHdrAdminOverview(),
      getHdrAdminQueue({ limit: 40, status: queueFilter || null }),
      getHdrAdminPipeline(),
    ]);
    if (!ov.success) setError(ov.error || 'Failed to load overview');
    else setOverview(ov.overview || null);
    if (q.success) setQueueJobs(q.jobs || []);
    if (pipe.success && pipe.setting) setPipelineSetting(pipe.setting);
    setLoading(false);
  };

  const switchPipeline = async (next: HdrMergePipeline) => {
    if (pipelineSaving || pipelineSetting?.pipeline === next) return;
    const currentLabel = next === 'autopix' ? 'Autoenhance' : 'Autopix';
    const nextLabel = next === 'autopix' ? 'Autopix' : 'Autoenhance';
    const confirmed = window.confirm(
      `Lock HDR processing to ${nextLabel}?\n\n` +
        `New uploads and queued jobs will stay on ${nextLabel} until you switch again. ` +
        `${currentLabel} will not be used unless you choose it here.`
    );
    if (!confirmed) return;
    setPipelineSaving(true);
    setPipelineMessage(null);
    const result = await setHdrAdminPipeline(next);
    setPipelineSaving(false);
    if (!result.success) {
      setError(result.error || 'Could not switch processor');
      return;
    }
    setPipelineSetting((prev) => ({
      pipeline: result.pipeline || next,
      updated_at: new Date().toISOString(),
      updated_by_email: prev?.updated_by_email || user.email,
    }));
    const retargeted = result.retargeted ?? 0;
    setPipelineMessage(
      retargeted > 0
        ? `Locked to ${next === 'autopix' ? 'Autopix' : 'Autoenhance'}. ${retargeted} queued job${retargeted === 1 ? '' : 's'} retargeted.`
        : `Locked to ${next === 'autopix' ? 'Autopix' : 'Autoenhance'}. New uploads will stay on this processor.`
    );
    loadOverview();
  };

  const loadImages = async (
    page = imagesPage,
    overrides: { type?: string; listingId?: string } = {}
  ) => {
    setLoading(true);
    setError(null);
    const type = overrides.type ?? imageTypeFilter;
    const listingId = overrides.listingId ?? userListingFilter;
    const result = await getHdrAdminImages({
      limit: pageSize,
      offset: page * pageSize,
      processingType: type || null,
      search: selectedUserId ? null : imageSearch.trim() || null,
      userId: selectedUserId,
      listingId: selectedUserId ? listingId || null : null,
    });
    if (!result.success) setError(result.error || 'Failed to load images');
    else {
      setImages(result.images || []);
      setImagesTotal(result.total || 0);
    }
    setLoading(false);
  };

  const loadUsers = async (page = usersPage, sort = userSort) => {
    setLoading(true);
    setError(null);
    const result = await getHdrAdminUsers({
      limit: usersPageSize,
      offset: page * usersPageSize,
      search: userSearch.trim() || null,
      sort,
    });
    if (!result.success) setError(result.error || 'Failed to load users');
    else {
      setUsers(result.users || []);
      setUsersTotal(result.total || 0);
    }
    setLoading(false);
  };

  const loadUserDetail = async (userId: string) => {
    const result = await getHdrAdminUserDetail(userId);
    if (!result.success) {
      setError(result.error === 'USER_NOT_FOUND' ? 'User not found' : result.error || 'Failed to load user');
      return;
    }
    setUserDetail(result.user || null);
    setUserListings(result.listings || []);
  };

  const openUser = (userId: string) => {
    setLightboxIndex(null);
    navigate(`/admin/users/${userId}`);
  };

  const loadSales = async () => {
    setLoading(true);
    setError(null);
    const [result, profit, plans] = await Promise.all([
      getHdrAdminSalesMetrics(periodStart(salesPeriod)),
      adminGetHdrProfitability(),
      adminGetHdrPlans(),
    ]);
    if (!result.success) setError(result.error || 'Failed to load sales');
    else setSales(result.metrics || null);
    if (profit.success) setHdrProfit(profit.metrics || null);
    if (plans.success) {
      setHdrPlans(plans.plans || []);
      if (plans.processing_cost_usd != null) setProcessingCost(String(plans.processing_cost_usd));
      if (plans.gbp_usd_rate != null) setGbpUsdRate(String(plans.gbp_usd_rate));
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!activeTab) return;
    setLightboxIndex(null);
    if (activeTab === 'overview') loadOverview();
    else if (activeTab === 'images') {
      setImages([]);
      setImagesPage(0);
      loadImages(0);
    } else if (activeTab === 'users') {
      if (selectedUserId) {
        setImages([]);
        setImagesPage(0);
        setUserDetail(null);
        setUserListings([]);
        setUserListingFilter('');
        loadUserDetail(selectedUserId);
        loadImages(0, { listingId: '' });
      } else {
        loadUsers();
      }
    } else if (activeTab === 'sales') loadSales();
    // 'engine' tab self-loads inside <HdrEngineAdmin />
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedUserId]);

  useEffect(() => {
    if (resolvedTab === 'overview') loadOverview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queueFilter]);

  useEffect(() => {
    if (resolvedTab === 'sales') loadSales();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salesPeriod]);

  if (!activeTab) {
    return <Navigate to="/admin/processing" replace />;
  }

  const refresh = () => {
    if (activeTab === 'overview') loadOverview();
    else if (activeTab === 'images') loadImages();
    else if (activeTab === 'users') {
      if (selectedUserId) {
        loadUserDetail(selectedUserId);
        loadImages();
      } else loadUsers();
    } else if (activeTab === 'sales') loadSales();
    // 'engine' refreshes via its own controls
  };

  const imageTypeSelect = (
    <select
      value={imageTypeFilter}
      onChange={(e) => {
        setImageTypeFilter(e.target.value);
        setImagesPage(0);
        loadImages(0, { type: e.target.value });
      }}
      className="bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-sm text-slate-200"
    >
      <option value="">All types</option>
      <option value="hdr_merge">HDR merge</option>
      <option value="blue_sky">Blue sky</option>
      <option value="verticals">Verticals</option>
      <option value="twilight">Twilight</option>
      <option value="auto_remove">Auto remove</option>
      <option value="grass">Grass</option>
      <option value="staging">Staging</option>
      <option value="curves">Curves</option>
      <option value="photoedit">PhotoEdit</option>
    </select>
  );

  const imageBrowser = (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-slate-500">{imagesTotal} images</p>
        <div className="flex items-center gap-2" title="Images per row">
          <span className="text-xs text-slate-500">Size</span>
          <input
            type="range"
            min={1}
            max={8}
            step={1}
            value={imageGridCols}
            onChange={(e) => updateImageGridCols(Number(e.target.value))}
            className="w-32 accent-brand-500"
          />
          <span className="text-xs text-slate-400 whitespace-nowrap">{imageGridCols} / row</span>
        </div>
      </div>

      {loading && images.length === 0 ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-8 h-8 text-brand-500 animate-spin" />
        </div>
      ) : images.length === 0 ? (
        <div className="text-center py-16 text-slate-500 text-sm">No images found</div>
      ) : (
        <div
          className="grid gap-2"
          style={{ gridTemplateColumns: `repeat(${imageGridCols}, minmax(0, 1fr))` }}
        >
          {images.map((img, i) => (
            <button
              key={img.id}
              type="button"
              onClick={() => setLightboxIndex(i)}
              className="group text-left rounded-lg overflow-hidden border border-slate-800 bg-slate-900 hover:border-brand-500/40 transition-colors cursor-zoom-in"
              title={`${img.listing_name} · ${img.user_email || 'Unknown'}`}
            >
              <div className="aspect-[4/3] bg-slate-800 relative">
                <img
                  src={getThumbnailUrl(img.url, { width: imageThumbWidth, height: Math.round(imageThumbWidth * 0.75), quality: 55 }) || img.url}
                  alt=""
                  className="w-full h-full object-cover"
                  loading="lazy"
                  decoding="async"
                />
                <span className="absolute top-1 left-1 text-[9px] font-medium px-1 py-0.5 rounded bg-black/70 text-slate-200 truncate max-w-[55%]">
                  {img.processing_type}
                </span>
                {img.pipeline && (
                  <span className="absolute top-1 right-1 text-[9px] font-medium px-1 py-0.5 rounded bg-black/70 text-brand-200">
                    {img.pipeline}
                  </span>
                )}
              </div>
              <div className="p-1.5">
                <p className="text-[10px] text-white truncate leading-tight">{img.listing_name}</p>
                {!selectedUserId && (
                  <p className="text-[9px] text-slate-500 truncate mt-0.5">
                    {img.user_email || 'Unknown user'}
                  </p>
                )}
                <p className="text-[9px] text-slate-600 mt-0.5">{formatShortDate(img.created_at)}</p>
              </div>
            </button>
          ))}
        </div>
      )}

      {imagesTotal > pageSize && (
        <Pagination
          page={imagesPage}
          pageSize={pageSize}
          total={imagesTotal}
          loading={loading}
          onPage={(next) => {
            setImagesPage(next);
            loadImages(next);
          }}
        />
      )}
    </>
  );

  const tabs: { id: AdminTab; label: string; icon: React.ReactNode; path: string }[] = [
    { id: 'overview', label: 'Processing', icon: <Activity size={16} />, path: '/admin/processing' },
    { id: 'users', label: 'Users', icon: <Users size={16} />, path: '/admin/users' },
    { id: 'images', label: 'Images', icon: <Images size={16} />, path: '/admin/images' },
    { id: 'sales', label: 'Costs & Profit', icon: <DollarSign size={16} />, path: '/admin/costs' },
    { id: 'engine', label: 'HDR Engine', icon: <Sparkles size={16} />, path: '/admin/hdr-engine' },
  ];

  return (
    <div className="fixed inset-0 z-[80] bg-slate-950 flex">
      <div className="flex-1 flex overflow-hidden border-0 sm:m-0 bg-slate-950">
        {/* Side nav */}
        <aside className="w-56 shrink-0 border-r border-slate-800 bg-slate-900/60 p-4 flex flex-col">
          <div className="flex items-center gap-2 mb-6 px-1">
            <div className="w-8 h-8 rounded-lg bg-brand-500/20 text-brand-400 flex items-center justify-center">
              <Shield size={16} />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-white truncate">HDR Admin</p>
              <p className="text-[11px] text-slate-500 capitalize">{adminRole}</p>
            </div>
          </div>

          <nav className="space-y-1 flex-1">
            {tabs.map((tab) => (
              <Link
                key={tab.id}
                to={tab.path}
                className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'bg-brand-600 text-white'
                    : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                }`}
              >
                {tab.icon}
                {tab.label}
              </Link>
            ))}
          </nav>

          <p className="text-[11px] text-slate-600 px-1 mt-4 truncate">{user.email}</p>
        </aside>

        {/* Main */}
        <div className="flex-1 flex flex-col min-w-0">
          <header className="h-14 shrink-0 border-b border-slate-800 px-5 flex items-center justify-between gap-3">
            <div>
              <h2 className="text-base font-semibold text-white">
                {activeTab === 'overview' && 'Processing queue'}
                {activeTab === 'users' && !selectedUserId && 'Users'}
                {activeTab === 'users' && selectedUserId && (userDetail?.email || 'User')}
                {activeTab === 'images' && 'User images'}
                {activeTab === 'sales' && 'Costs vs profit'}
                {activeTab === 'engine' && 'ReHub HDR Engine'}
              </h2>
              <p className="text-xs text-slate-500">
                {activeTab === 'overview' && 'Live queue depth and job status across all users'}
                {activeTab === 'users' && !selectedUserId && 'Everyone who has used HDR — open a user to see only their images'}
                {activeTab === 'users' && selectedUserId && 'Listings and processed images for this account'}
                {activeTab === 'images' && 'Browse processed results from every account'}
                {activeTab === 'sales' && 'HDR-attributed revenue vs estimated vendor cost'}
                {activeTab === 'engine' && 'Overview, training datasets, models, image comparison, benchmarks & rollout control'}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={refresh}
                disabled={loading}
                className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors disabled:opacity-50"
                title="Refresh"
              >
                <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
              </button>
              <button
                onClick={() => navigate('/listings')}
                className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
                title="Back to listings"
              >
                <X size={18} />
              </button>
            </div>
          </header>

          <main className="flex-1 overflow-auto p-5">
            {error && (
              <div className="mb-4 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
                {error}
                {error.includes('function') || error.includes('does not exist') || error.includes('relation') ? (
                  <span className="block mt-1 text-red-400/80 text-xs">
                    If this persists after deploy, re-run the latest HDR admin migration in Supabase.
                  </span>
                ) : null}
              </div>
            )}

            {loading && !overview && !pipelineSetting && activeTab === 'overview' && (
              <div className="flex justify-center py-20">
                <Loader2 className="w-8 h-8 text-brand-500 animate-spin" />
              </div>
            )}

            {/* OVERVIEW */}
            {activeTab === 'overview' && (overview || pipelineSetting) && (
              <div className="space-y-6 max-w-6xl">
                <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-5">
                  <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                    <div>
                      <h3 className="text-sm font-semibold text-white">HDR processor</h3>
                      <p className="text-xs text-slate-500 mt-1 max-w-xl">
                        This choice stays locked. New uploads, Flow, and Photoplan jobs all use the
                        selected provider until you switch it here. Autoenhance remains the instant backup.
                      </p>
                    </div>
                    {pipelineSetting?.updated_at && (
                      <p className="text-[11px] text-slate-600">
                        Last change {formatShortDate(pipelineSetting.updated_at)}
                        {pipelineSetting.updated_by_email ? ` · ${pipelineSetting.updated_by_email}` : ''}
                      </p>
                    )}
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {HDR_PRIMARY_PIPELINE_OPTIONS.map((option) => {
                      const active = pipelineSetting?.pipeline === option.value;
                      return (
                        <button
                          key={option.value}
                          type="button"
                          disabled={pipelineSaving}
                          onClick={() => switchPipeline(option.value)}
                          className={`text-left rounded-xl border px-4 py-3 transition-colors disabled:opacity-60 ${
                            active
                              ? 'border-brand-500 bg-brand-500/15'
                              : 'border-slate-800 bg-slate-950 hover:border-slate-600'
                          }`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <p className="text-sm font-semibold text-white">{option.label}</p>
                            {active && (
                              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide text-brand-300 font-medium">
                                <Lock size={10} />
                                Locked
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-slate-400 mt-1">{option.hint}</p>
                          {option.value === 'autoenhance' && (
                            <p className="text-[11px] text-slate-500 mt-2">Use this if Autopix is down or slow.</p>
                          )}
                        </button>
                      );
                    })}
                  </div>
                  {pipelineSaving && (
                    <p className="text-xs text-slate-400 mt-3 flex items-center gap-2">
                      <Loader2 size={12} className="animate-spin" />
                      Switching processor…
                    </p>
                  )}
                  {pipelineMessage && (
                    <p className="text-xs text-emerald-300 mt-3">{pipelineMessage}</p>
                  )}
                </div>

                {overview && (
                <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <StatCard
                    label="Queued"
                    value={overview.queue.queued}
                    icon={<Clock size={20} className="text-slate-400" />}
                  />
                  <StatCard
                    label="Processing now"
                    value={overview.queue.processing}
                    icon={<Loader2 size={20} className="text-blue-400" />}
                    accent="text-blue-300"
                  />
                  <StatCard
                    label="Completed today"
                    value={overview.queue.completed_today}
                    icon={<CheckCircle2 size={20} className="text-emerald-400" />}
                    accent="text-emerald-300"
                  />
                  <StatCard
                    label="Failed today"
                    value={overview.queue.failed_today}
                    icon={<XCircle size={20} className="text-red-400" />}
                    accent="text-red-300"
                  />
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <StatCard label="Media processing" value={overview.media.processing} />
                  <StatCard label="Media ready" value={overview.media.ready} />
                  <StatCard label="Images today" value={overview.processed_images.today} />
                  <StatCard label="Active users" value={overview.users_with_listings} />
                </div>

                {overview.queue.stuck > 0 && (
                  <div className="flex items-start gap-3 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-200">
                    <AlertTriangle size={18} className="shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium">{overview.queue.stuck} stuck job{overview.queue.stuck === 1 ? '' : 's'}</p>
                      <p className="text-amber-300/80 text-xs mt-0.5">
                        Processing for over 30 minutes — check the worker.
                      </p>
                    </div>
                  </div>
                )}

                <div className="rounded-2xl border border-slate-800 bg-slate-900/50 overflow-hidden">
                  <div className="px-4 py-3 border-b border-slate-800 flex flex-wrap items-center gap-3 justify-between">
                    <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                      <Layers size={16} className="text-brand-400" />
                      Recent jobs
                    </h3>
                    <select
                      value={queueFilter}
                      onChange={(e) => setQueueFilter(e.target.value)}
                      className="bg-slate-800 border border-slate-700 rounded-lg text-sm text-slate-200 px-3 py-1.5"
                    >
                      <option value="">All statuses</option>
                      <option value="queued">Queued</option>
                      <option value="processing">Processing</option>
                      <option value="failed">Failed</option>
                      <option value="completed">Completed</option>
                    </select>
                  </div>

                  {queueJobs.length === 0 ? (
                    <p className="text-sm text-slate-500 p-8 text-center">No jobs in this view</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left text-xs text-slate-500 border-b border-slate-800">
                            <th className="px-4 py-2.5 font-medium">Status</th>
                            <th className="px-4 py-2.5 font-medium">User</th>
                            <th className="px-4 py-2.5 font-medium">Listing</th>
                            <th className="px-4 py-2.5 font-medium">Type</th>
                            <th className="px-4 py-2.5 font-medium">Pipeline</th>
                            <th className="px-4 py-2.5 font-medium">Created</th>
                          </tr>
                        </thead>
                        <tbody>
                          {queueJobs.map((job) => (
                            <tr key={job.id} className="border-b border-slate-800/80 hover:bg-slate-800/40">
                              <td className="px-4 py-2.5">
                                <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${queueStatusStyles[job.status] || 'bg-slate-700 text-slate-300'}`}>
                                  {job.status}
                                </span>
                                {job.error_message && (
                                  <p className="text-[11px] text-red-400 mt-1 max-w-[200px] truncate" title={job.error_message}>
                                    {job.error_message}
                                  </p>
                                )}
                              </td>
                              <td className="px-4 py-2.5 text-slate-300 truncate max-w-[180px]">
                                {job.user_id ? (
                                  <Link to={`/admin/users/${job.user_id}`} className="hover:text-brand-300 hover:underline">
                                    {job.user_email || job.user_id}
                                  </Link>
                                ) : (
                                  job.user_email || '—'
                                )}
                              </td>
                              <td className="px-4 py-2.5 text-slate-300 truncate max-w-[160px]">
                                {job.listing_name}
                              </td>
                              <td className="px-4 py-2.5 text-slate-400">{job.job_type}</td>
                              <td className="px-4 py-2.5 text-slate-400">
                                {(job.metadata?.pipeline as string) || '—'}
                              </td>
                              <td className="px-4 py-2.5 text-slate-500 whitespace-nowrap">
                                {formatShortDate(job.created_at)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
                </>
                )}
              </div>
            )}

            {/* IMAGES */}
            {activeTab === 'images' && (
              <div className="space-y-4">
                <div className="flex flex-wrap gap-2 items-center">
                  <input
                    value={imageSearch}
                    onChange={(e) => setImageSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        setImagesPage(0);
                        loadImages(0);
                      }
                    }}
                    placeholder="Search email or listing…"
                    className="flex-1 min-w-[200px] bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-sm text-white placeholder:text-slate-500"
                  />
                  {imageTypeSelect}
                  <button
                    onClick={() => {
                      setImagesPage(0);
                      loadImages(0);
                    }}
                    className="px-4 py-2 rounded-xl bg-brand-600 hover:bg-brand-500 text-white text-sm font-medium"
                  >
                    Search
                  </button>
                </div>

                {imageBrowser}
              </div>
            )}

            {/* USERS — list */}
            {activeTab === 'users' && !selectedUserId && (
              <div className="space-y-4 max-w-6xl">
                <div className="flex flex-wrap gap-2 items-center">
                  <input
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        setUsersPage(0);
                        loadUsers(0);
                      }
                    }}
                    placeholder="Search email or name…"
                    className="flex-1 min-w-[200px] bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-sm text-white placeholder:text-slate-500"
                  />
                  <select
                    value={userSort}
                    onChange={(e) => {
                      const sort = e.target.value as HdrAdminUserSort;
                      setUserSort(sort);
                      setUsersPage(0);
                      loadUsers(0, sort);
                    }}
                    className="bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-sm text-slate-200"
                  >
                    <option value="last_activity">Recently active</option>
                    <option value="images">Most images</option>
                    <option value="listings">Most listings</option>
                    <option value="joined">Newest accounts</option>
                    <option value="email">Email A–Z</option>
                  </select>
                  <button
                    onClick={() => {
                      setUsersPage(0);
                      loadUsers(0);
                    }}
                    className="px-4 py-2 rounded-xl bg-brand-600 hover:bg-brand-500 text-white text-sm font-medium"
                  >
                    Search
                  </button>
                </div>

                <p className="text-xs text-slate-500">{usersTotal} users</p>

                {loading && users.length === 0 ? (
                  <div className="flex justify-center py-16">
                    <Loader2 className="w-8 h-8 text-brand-500 animate-spin" />
                  </div>
                ) : users.length === 0 ? (
                  <div className="text-center py-16 text-slate-500 text-sm">No users found</div>
                ) : (
                  <div className="rounded-2xl border border-slate-800 bg-slate-900/50 overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-xs text-slate-500 border-b border-slate-800">
                          <th className="px-4 py-2.5 font-medium">User</th>
                          <th className="px-4 py-2.5 font-medium">HDR plan</th>
                          <th className="px-4 py-2.5 font-medium text-right">Listings</th>
                          <th className="px-4 py-2.5 font-medium text-right">Images</th>
                          <th className="px-4 py-2.5 font-medium">Last activity</th>
                          <th className="px-4 py-2.5 font-medium">Joined</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.map((u) => (
                          <tr
                            key={u.id}
                            onClick={() => openUser(u.id)}
                            className="border-b border-slate-800/80 hover:bg-slate-800/40 cursor-pointer"
                          >
                            <td className="px-4 py-2.5 max-w-[260px]">
                              <p className="text-white truncate">{u.email || 'Unknown user'}</p>
                              {u.full_name && <p className="text-[11px] text-slate-500 truncate">{u.full_name}</p>}
                            </td>
                            <td className="px-4 py-2.5 text-slate-300 whitespace-nowrap">
                              <PlanSummary user={u} />
                            </td>
                            <td className="px-4 py-2.5 text-right text-slate-300">{u.listings_count}</td>
                            <td className="px-4 py-2.5 text-right text-slate-300">{u.images_count}</td>
                            <td className="px-4 py-2.5 text-slate-500 whitespace-nowrap">
                              {u.last_activity_at ? formatShortDate(u.last_activity_at) : '—'}
                            </td>
                            <td className="px-4 py-2.5 text-slate-500 whitespace-nowrap">
                              {u.joined_at ? formatShortDate(u.joined_at) : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {usersTotal > usersPageSize && (
                  <Pagination
                    page={usersPage}
                    pageSize={usersPageSize}
                    total={usersTotal}
                    loading={loading}
                    onPage={(next) => {
                      setUsersPage(next);
                      loadUsers(next);
                    }}
                  />
                )}
              </div>
            )}

            {/* USERS — single user */}
            {activeTab === 'users' && selectedUserId && (
              <div className="space-y-4">
                <Link
                  to="/admin/users"
                  className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-white"
                >
                  <ArrowLeft size={14} />
                  All users
                </Link>

                {userDetail && (
                  <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-4 flex flex-wrap items-center gap-x-8 gap-y-3">
                    <div className="min-w-0">
                      <p className="text-white font-medium truncate">{userDetail.email || 'Unknown user'}</p>
                      {userDetail.full_name && <p className="text-xs text-slate-500 truncate">{userDetail.full_name}</p>}
                    </div>
                    <div>
                      <p className="text-[11px] text-slate-500">HDR plan</p>
                      <p className="text-sm text-slate-200">
                        <PlanSummary user={userDetail} />
                      </p>
                    </div>
                    <div>
                      <p className="text-[11px] text-slate-500">Listings</p>
                      <p className="text-sm text-slate-200">{userListings.length}</p>
                    </div>
                    <div>
                      <p className="text-[11px] text-slate-500">Joined</p>
                      <p className="text-sm text-slate-200">
                        {userDetail.joined_at ? formatShortDate(userDetail.joined_at) : '—'}
                      </p>
                    </div>
                    <p className="text-[11px] text-slate-600 font-mono ml-auto select-all">{userDetail.id}</p>
                  </div>
                )}

                <div className="flex flex-wrap gap-2 items-center">
                  <select
                    value={userListingFilter}
                    onChange={(e) => {
                      setUserListingFilter(e.target.value);
                      setImagesPage(0);
                      loadImages(0, { listingId: e.target.value });
                    }}
                    className="flex-1 min-w-[200px] max-w-md bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-sm text-slate-200"
                  >
                    <option value="">All listings</option>
                    {userListings.map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.name} ({l.images_count})
                      </option>
                    ))}
                  </select>
                  {imageTypeSelect}
                </div>

                {imageBrowser}
              </div>
            )}

            {/* SALES */}
            {activeTab === 'sales' && (
              <div className="space-y-6 max-w-5xl">
                {hdrProfit && (
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-white">HDR subscription profitability</h3>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div className="rounded-2xl border border-slate-800 bg-slate-900 p-5">
                        <p className="text-sm text-slate-400">Active HDR subscribers</p>
                        <p className="text-2xl font-bold text-white mt-1">{hdrProfit.active_subscribers}</p>
                      </div>
                      <div className="rounded-2xl border border-slate-800 bg-slate-900 p-5">
                        <p className="text-sm text-slate-400">HDR MRR</p>
                        <p className="text-2xl font-bold text-emerald-400 mt-1">
                          £{hdrProfit.mrr_gbp.toFixed(0)} / €{(hdrProfit.mrr_eur ?? 0).toFixed(0)} / ${hdrProfit.mrr_usd.toFixed(0)}
                        </p>
                        <p className="text-xs text-slate-500 mt-1">
                          GBP+USD ${hdrProfit.mrr_usd_equivalent.toFixed(0)} equivalent · EUR reported separately
                        </p>
                      </div>
                      <div className="rounded-2xl border border-slate-800 bg-slate-900 p-5">
                        <p className="text-sm text-slate-400">Images this month</p>
                        <p className="text-2xl font-bold text-white mt-1">{hdrProfit.images_processed_this_month}</p>
                        <p className="text-xs text-slate-500 mt-1">
                          avg {hdrProfit.avg_images_per_subscriber} / subscriber
                        </p>
                      </div>
                      <div className="rounded-2xl border border-slate-800 bg-slate-900 p-5">
                        <p className="text-sm text-slate-400">Gross contribution</p>
                        <p className={`text-2xl font-bold mt-1 ${hdrProfit.gross_contribution_usd >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                          ${hdrProfit.gross_contribution_usd.toFixed(0)}
                        </p>
                        <p className="text-xs text-slate-500 mt-1">{hdrProfit.gross_margin_pct}% margin</p>
                      </div>
                    </div>
                    <p className="text-xs text-slate-500">
                      Processing cost ${hdrProfit.processing_cost_usd.toFixed(2)} × {hdrProfit.images_processed_this_month} images = ${hdrProfit.estimated_processing_cost_usd.toFixed(2)}. GBP converted at {hdrProfit.gbp_usd_rate} for the combined view only.
                    </p>
                    {hdrProfit.subscribers_by_plan?.length > 0 && (
                      <div className="rounded-2xl border border-slate-800 overflow-hidden">
                        <table className="w-full text-sm">
                          <thead className="bg-slate-900 text-slate-400">
                            <tr>
                              <th className="text-left p-3">Plan</th>
                              <th className="text-right p-3">Subscribers</th>
                              <th className="text-right p-3">Images this month</th>
                            </tr>
                          </thead>
                          <tbody>
                            {hdrProfit.subscribers_by_plan.map((row) => (
                              <tr key={row.plan_id || row.name} className="border-t border-slate-800">
                                <td className="p-3 text-white">{row.name}</td>
                                <td className="p-3 text-right text-slate-300">{row.subscribers}</td>
                                <td className="p-3 text-right text-slate-300">{row.images_this_month}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5 space-y-4">
                  <h3 className="text-lg font-semibold text-white">HDR pricing configuration</h3>
                  <p className="text-sm text-slate-400">
                    Change prices and the processing cost here. New checkouts pick up the new amounts automatically — no rebuild.
                  </p>
                  <div className="flex flex-wrap gap-4">
                    <label className="text-sm text-slate-300">
                      HDR processing cost per image (USD)
                      <input
                        value={processingCost}
                        onChange={(e) => setProcessingCost(e.target.value)}
                        className="mt-1 block w-40 rounded-lg bg-slate-800 border border-slate-700 px-3 py-2 text-white"
                      />
                    </label>
                    <label className="text-sm text-slate-300">
                      GBP→USD rate (reporting only)
                      <input
                        value={gbpUsdRate}
                        onChange={(e) => setGbpUsdRate(e.target.value)}
                        className="mt-1 block w-40 rounded-lg bg-slate-800 border border-slate-700 px-3 py-2 text-white"
                      />
                    </label>
                    <button
                      type="button"
                      disabled={pricingSaving}
                      onClick={async () => {
                        setPricingSaving(true);
                        await adminSetHdrSetting('processing_cost_usd', processingCost);
                        await adminSetHdrSetting('gbp_usd_rate', gbpUsdRate);
                        await loadSales();
                        setPricingSaving(false);
                      }}
                      className="self-end rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white"
                    >
                      Save costs
                    </button>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="text-slate-400">
                        <tr>
                          <th className="text-left p-2">Plan</th>
                          <th className="text-right p-2">Images</th>
                          <th className="text-right p-2">£</th>
                          <th className="text-right p-2">€</th>
                          <th className="text-right p-2">$</th>
                          <th className="text-left p-2">Stripe GBP</th>
                          <th className="text-left p-2">Stripe EUR</th>
                          <th className="text-left p-2">Stripe USD</th>
                        </tr>
                      </thead>
                      <tbody>
                        {hdrPlans.map((plan) => (
                          <tr key={plan.id} className="border-t border-slate-800">
                            <td className="p-2 text-white">{plan.name}</td>
                            <td className="p-2">
                              <input
                                defaultValue={plan.imageAllowance}
                                className="w-20 rounded bg-slate-800 border border-slate-700 px-2 py-1 text-right text-white"
                                onBlur={(e) =>
                                  adminUpsertHdrPlan({ id: plan.id, imageAllowance: Number(e.target.value) })
                                }
                              />
                            </td>
                            <td className="p-2">
                              <input
                                defaultValue={(plan.priceGbpMinor / 100).toFixed(0)}
                                className="w-20 rounded bg-slate-800 border border-slate-700 px-2 py-1 text-right text-white"
                                onBlur={(e) =>
                                  adminUpsertHdrPlan({ id: plan.id, priceGbpMinor: Math.round(Number(e.target.value) * 100) })
                                }
                              />
                            </td>
                            <td className="p-2">
                              <input
                                defaultValue={((plan.priceEurMinor ?? 0) / 100).toFixed(0)}
                                className="w-20 rounded bg-slate-800 border border-slate-700 px-2 py-1 text-right text-white"
                                onBlur={(e) =>
                                  adminUpsertHdrPlan({ id: plan.id, priceEurMinor: Math.round(Number(e.target.value) * 100) })
                                }
                              />
                            </td>
                            <td className="p-2">
                              <input
                                defaultValue={(plan.priceUsdMinor / 100).toFixed(0)}
                                className="w-20 rounded bg-slate-800 border border-slate-700 px-2 py-1 text-right text-white"
                                onBlur={(e) =>
                                  adminUpsertHdrPlan({ id: plan.id, priceUsdMinor: Math.round(Number(e.target.value) * 100) })
                                }
                              />
                            </td>
                            <td className="p-2">
                              <input
                                defaultValue={plan.stripePriceIdGbp || ''}
                                placeholder="auto"
                                className="w-36 rounded bg-slate-800 border border-slate-700 px-2 py-1 text-white"
                                onBlur={(e) =>
                                  adminUpsertHdrPlan({ id: plan.id, stripePriceIdGbp: e.target.value || null })
                                }
                              />
                            </td>
                            <td className="p-2">
                              <input
                                defaultValue={plan.stripePriceIdEur || ''}
                                placeholder="auto"
                                className="w-36 rounded bg-slate-800 border border-slate-700 px-2 py-1 text-white"
                                onBlur={(e) =>
                                  adminUpsertHdrPlan({ id: plan.id, stripePriceIdEur: e.target.value || null })
                                }
                              />
                            </td>
                            <td className="p-2">
                              <input
                                defaultValue={plan.stripePriceIdUsd || ''}
                                placeholder="auto"
                                className="w-36 rounded bg-slate-800 border border-slate-700 px-2 py-1 text-white"
                                onBlur={(e) =>
                                  adminUpsertHdrPlan({ id: plan.id, stripePriceIdUsd: e.target.value || null })
                                }
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-4 flex flex-wrap items-center gap-3">
                  <span className="text-sm text-slate-400">Period:</span>
                  {(['today', '7d', '30d', 'all'] as SalesPeriod[]).map((period) => (
                    <button
                      key={period}
                      onClick={() => setSalesPeriod(period)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        salesPeriod === period
                          ? 'bg-brand-600 text-white'
                          : 'bg-slate-800 text-slate-400 hover:text-white'
                      }`}
                    >
                      {period === 'today'
                        ? 'Today'
                        : period === '7d'
                          ? 'Last 7 days'
                          : period === '30d'
                            ? 'Last 30 days'
                            : 'All time'}
                    </button>
                  ))}
                </div>

                {loading && !sales ? (
                  <div className="flex justify-center py-16">
                    <Loader2 className="w-8 h-8 text-brand-500 animate-spin" />
                  </div>
                ) : sales ? (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="rounded-2xl border border-slate-800 bg-slate-900 p-5">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="text-sm text-slate-400">HDR revenue</p>
                            <p className="text-2xl font-bold text-emerald-400 mt-1">
                              {formatUsdGbp(sales.attributed_revenue_usd).str}
                            </p>
                            <p className="text-xs text-slate-500 mt-1">
                              {sales.credits_used} credits @ ${sales.credit_price_usd}/cr
                            </p>
                          </div>
                          <DollarSign size={28} className="text-emerald-400" />
                        </div>
                      </div>

                      <div className="rounded-2xl border border-slate-800 bg-slate-900 p-5">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="text-sm text-slate-400">Est. API cost</p>
                            <p className="text-2xl font-bold text-red-400 mt-1">
                              {formatUsdGbp(sales.total_cost_usd).str}
                            </p>
                            <p className="text-xs text-slate-500 mt-1">
                              {sales.completed_jobs} completed jobs
                            </p>
                          </div>
                          <Target size={28} className="text-red-400" />
                        </div>
                      </div>

                      <div
                        className={`rounded-2xl border bg-slate-900 p-5 ${
                          sales.gross_margin_usd >= 0 ? 'border-emerald-800' : 'border-red-800'
                        }`}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="text-sm text-slate-400">Gross margin</p>
                            <p
                              className={`text-2xl font-bold mt-1 ${
                                sales.gross_margin_usd >= 0 ? 'text-emerald-400' : 'text-red-400'
                              }`}
                            >
                              {formatUsdGbp(sales.gross_margin_usd).str}
                            </p>
                            <p
                              className={`text-xs mt-1 ${
                                sales.gross_margin_pct >= 0 ? 'text-emerald-400' : 'text-red-400'
                              }`}
                            >
                              {sales.gross_margin_pct >= 0 ? '+' : ''}
                              {sales.gross_margin_pct}%
                            </p>
                          </div>
                          <Percent
                            size={28}
                            className={sales.gross_margin_usd >= 0 ? 'text-emerald-400' : 'text-red-400'}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <MiniStat label="Credits used" value={String(sales.credits_used)} />
                      <MiniStat label="Credits refunded" value={String(sales.credits_refunded)} />
                      <MiniStat
                        label="Failed rate"
                        value={`${sales.failed_rate_pct}%`}
                        sub={`${sales.failed_jobs} / ${sales.total_jobs}`}
                      />
                      <MiniStat
                        label="Avg cost / image"
                        value={formatUsdGbp(sales.avg_cost_per_image, 4).str}
                      />
                    </div>

                    <div className="rounded-2xl border border-slate-800 bg-slate-900 p-5">
                      <h3 className="text-sm font-semibold text-white mb-4">Cost by pipeline</h3>
                      <div className="grid sm:grid-cols-3 gap-3">
                        {(
                          [
                            ['autoenhance', 'Autoenhance'],
                            ['autopix', 'Autopix'],
                            ['gemini', 'Gemini'],
                            ['fal', 'fal.ai'],
                          ] as const
                        ).map(([key, label]) => {
                          const empty = { count: 0, cost_usd: 0, unit_cost_usd: 0 };
                          const bucket = sales.pipeline_breakdown[key] ?? empty;
                          return (
                            <div key={key} className="rounded-xl bg-slate-950 border border-slate-800 p-4">
                              <p className="text-xs text-slate-500">{label}</p>
                              <p className="text-lg font-semibold text-white mt-1">{bucket.count} images</p>
                              <p className="text-xs text-slate-400 mt-1">
                                {formatUsdGbp(bucket.cost_usd).str}
                                <span className="text-slate-600">
                                  {' '}
                                  · {formatUsdGbp(bucket.unit_cost_usd, 4).str}/img est.
                                </span>
                              </p>
                            </div>
                          );
                        })}
                      </div>
                      <p className="text-[11px] text-slate-600 mt-4">{sales.cost_notes}</p>
                    </div>

                    {sales.attributed_revenue_usd > 0 && (
                      <div className="rounded-xl bg-black border border-slate-800 px-4 py-3 text-sm">
                        {sales.gross_margin_pct >= 50 ? (
                          <p className="text-emerald-400 font-medium">Healthy margins on HDR volume</p>
                        ) : sales.gross_margin_pct >= 20 ? (
                          <p className="text-yellow-400 font-medium">Moderate margins — watch Autoenhance volume</p>
                        ) : sales.gross_margin_pct >= 0 ? (
                          <p className="text-orange-400 font-medium">Thin margins — consider Gemini fallback or price lift</p>
                        ) : (
                          <p className="text-red-400 font-medium">Negative margin — vendor cost exceeds attributed revenue</p>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-16 text-slate-500 text-sm">No sales data yet</div>
                )}
              </div>
            )}

            {/* HDR ENGINE */}
            {activeTab === 'engine' && <HdrEngineAdmin />}
          </main>
        </div>
      </div>

      {lightboxIndex !== null && images[lightboxIndex] && (
        <HdrImageLightbox
          images={images}
          index={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onNavigate={setLightboxIndex}
          onViewUser={selectedUserId ? undefined : openUser}
        />
      )}
    </div>
  );
};

function HdrImageLightbox({
  images,
  index,
  onClose,
  onNavigate,
  onViewUser,
}: {
  images: HdrAdminImage[];
  index: number;
  onClose: () => void;
  onNavigate: (index: number) => void;
  onViewUser?: (userId: string) => void;
}) {
  const img = images[index];
  const hasPrev = index > 0;
  const hasNext = index < images.length - 1;
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setLoaded(false);
  }, [img.id]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft' && hasPrev) onNavigate(index - 1);
      if (e.key === 'ArrowRight' && hasNext) onNavigate(index + 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index, hasPrev, hasNext, onClose, onNavigate]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const modelLabel = img.pipeline ? PIPELINE_MODEL_LABELS[img.pipeline] || img.pipeline : null;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <button
        type="button"
        onClick={onClose}
        className="absolute top-4 right-4 p-2 text-slate-400 hover:text-white bg-slate-800/80 hover:bg-slate-700 rounded-full transition-colors z-20"
        aria-label="Close"
      >
        <X size={22} />
      </button>

      {hasPrev && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onNavigate(index - 1);
          }}
          className="absolute left-3 top-1/2 -translate-y-1/2 p-3 text-slate-400 hover:text-white bg-slate-800/60 hover:bg-slate-700/80 rounded-full transition-colors z-20"
          aria-label="Previous"
        >
          <ChevronLeft size={26} />
        </button>
      )}
      {hasNext && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onNavigate(index + 1);
          }}
          className="absolute right-3 top-1/2 -translate-y-1/2 p-3 text-slate-400 hover:text-white bg-slate-800/60 hover:bg-slate-700/80 rounded-full transition-colors z-20"
          aria-label="Next"
        >
          <ChevronRight size={26} />
        </button>
      )}

      <div
        className="w-full max-w-5xl flex flex-col items-center gap-4"
        onClick={(e) => e.stopPropagation()}
      >
        <span className="text-slate-500 text-xs">
          {index + 1} / {images.length}
        </span>

        <div className="relative flex items-center justify-center min-h-[30vh]">
          {!loaded && (
            <div className="absolute inset-0 flex items-center justify-center">
              <Loader2 size={28} className="animate-spin text-brand-500" />
            </div>
          )}
          <img
            key={img.id}
            src={img.url}
            alt={img.listing_name}
            className={`max-w-full max-h-[65vh] object-contain rounded-lg shadow-2xl transition-opacity duration-200 ${loaded ? 'opacity-100' : 'opacity-0'}`}
            onLoad={() => setLoaded(true)}
          />
        </div>

        {/* User details & AI model */}
        <div className="w-full max-w-2xl bg-slate-900/90 border border-slate-800 rounded-xl px-4 py-3">
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 text-sm">
            <span className="flex items-center gap-1.5 text-white font-medium">
              <User size={14} className="text-slate-400" />
              {img.user_email || 'Unknown user'}
            </span>
            <span className="text-slate-300 truncate max-w-[240px]">{img.listing_name}</span>
            <span className="flex items-center gap-1.5 text-slate-400">
              <Clock size={13} className="text-slate-500" />
              {formatShortDate(img.created_at)}
            </span>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 text-xs mt-2 text-slate-400">
            <span className="px-1.5 py-0.5 rounded bg-slate-800 text-slate-300">{img.processing_type}</span>
            {modelLabel && (
              <span className="flex items-center gap-1.5" title="AI model / pipeline">
                <Sparkles size={12} className="text-brand-400" />
                {modelLabel}
              </span>
            )}
            {img.width && img.height && (
              <span className="text-slate-500">
                {img.width} × {img.height}px
              </span>
            )}
            {typeof img.processing_time_ms === 'number' && img.processing_time_ms > 0 && (
              <span className="text-slate-500">{(img.processing_time_ms / 1000).toFixed(1)}s</span>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-center gap-2">
          {onViewUser && img.user_id && (
            <button
              type="button"
              onClick={() => onViewUser(img.user_id)}
              className="px-3 py-1.5 rounded-lg text-sm font-medium bg-slate-800 text-slate-300 hover:bg-slate-700 hover:text-white transition-colors flex items-center gap-1.5"
            >
              <User size={14} />
              All images from this user
            </button>
          )}
          <a
            href={img.url}
            target="_blank"
            rel="noreferrer"
            className="px-3 py-1.5 rounded-lg text-sm font-medium bg-slate-800 text-slate-300 hover:bg-slate-700 hover:text-white transition-colors flex items-center gap-1.5"
          >
            <ExternalLink size={14} />
            Open full size
          </a>
        </div>
      </div>
    </div>
  );
}

function StatCard({
  label,
  value,
  icon,
  accent = 'text-white',
}: {
  label: string;
  value: number;
  icon?: React.ReactNode;
  accent?: string;
}) {
  return (
    <div className="rounded-2xl border border-slate-800 bg-slate-900 p-4">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-xs text-slate-500">{label}</p>
          <p className={`text-2xl font-bold mt-1 ${accent}`}>{value}</p>
        </div>
        {icon}
      </div>
    </div>
  );
}

function PlanSummary({ user }: { user: Pick<HdrAdminUser, 'plan_id' | 'plan_status' | 'allowance' | 'used'> }) {
  if (!user.plan_id) return <span className="text-slate-500">—</span>;
  return (
    <>
      <span className="capitalize">{user.plan_id}</span>
      {user.plan_status && user.plan_status !== 'active' && (
        <span className="text-amber-300/80"> · {user.plan_status}</span>
      )}
      {user.allowance != null && (
        <span className="text-slate-500">
          {' '}
          · {user.used ?? 0}/{user.allowance} used
        </span>
      )}
    </>
  );
}

function Pagination({
  page,
  pageSize,
  total,
  loading,
  onPage,
}: {
  page: number;
  pageSize: number;
  total: number;
  loading: boolean;
  onPage: (page: number) => void;
}) {
  return (
    <div className="flex items-center justify-between pt-2">
      <p className="text-xs text-slate-500">
        Showing {page * pageSize + 1}–{Math.min((page + 1) * pageSize, total)} of {total}
      </p>
      <div className="flex gap-2">
        <button
          disabled={page === 0 || loading}
          onClick={() => onPage(page - 1)}
          className="px-3 py-1.5 rounded-lg bg-slate-800 text-sm text-slate-300 disabled:opacity-40"
        >
          Previous
        </button>
        <button
          disabled={(page + 1) * pageSize >= total || loading}
          onClick={() => onPage(page + 1)}
          className="px-3 py-1.5 rounded-lg bg-slate-800 text-sm text-slate-300 disabled:opacity-40"
        >
          Next
        </button>
      </div>
    </div>
  );
}

function MiniStat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900 p-4">
      <p className="text-xs text-slate-500">{label}</p>
      <p className="text-lg font-bold text-white mt-1 break-all">{value}</p>
      {sub && <p className="text-[10px] text-slate-600 mt-0.5">{sub}</p>}
    </div>
  );
}

function formatShortDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export default AdminPanel;
